<template>
  <pre
    id="formula-block"
    v-dompurify-html="highlightedFormulaHtml"
    :class="{'is-text': text}"/>
</template>

<script>
import * as math from 'mathjs'

export default {
  name: 'FormulaHighlighterMinimal',
  props: {
    /**
     * @type {string} formulaString - Входящая строка формулы для парсинга и подсветки.
     */
    formulaString: {
      type: String,
      required: true
    },
    /**
     * Добавляем пробелы в форматирование формулы.
     * @type {boolean}
     */
    useSpace: {
      type: Boolean,
      default: false
    },
    /**
     * Без доп. форматирования
     */
    text: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    space() {
      return this.useSpace ? ' ' : ''
    },
    highlightedFormulaHtml() {
      if (!this.formulaString) {
        return ''
      }

      if (!this.growthBook.use_mathjs) {
        return this.formulaString
      }

      try {
        const node = math.parse(this.formulaString)
        return this.formatAndHighlightNode(node, 0)

      } catch (e) {
        return this.text ? this.formulaString : `<span class="error-highlight">${this.formulaString}</span>`
      }
    }
  },
  methods: {
    formatAndHighlightNode(node) {
      let result = ''

      switch (node.type) {
      case 'OperatorNode': {
        const op = node.op
        const isBinary = node.args.length === 2
        const opClass = 'highlight-operator'

        // Унарный минус/плюс перед числом (например, -5)
        if (!isBinary && ['-', '+'].includes(op) && node.args.length === 1 && node.args[0].type === 'ConstantNode') {
          return `<span class="${opClass}">${op}</span>${this.formatAndHighlightNode(node.args[0])}`
        }

        const leftContent = this.formatAndHighlightNode(node.args[0])
        const rightContent = isBinary ? this.formatAndHighlightNode(node.args[1]) : '' // Отступ для правого операнда

        if (isBinary) {
          result = `${leftContent}${this.space}<span class="${opClass}">${op}</span>${this.space}${rightContent}`
        } else {
          // Для унарных операторов (кроме унарного +/- перед числом, обработанного выше)
          result = `<span class="${opClass}">${op}</span>${leftContent}`
        }
        return result
      }

      case 'FunctionNode': {
        const functionName = node.name
        const funcClass = 'highlight-function'

        if (functionName === 'if' && node.args.length === 3) {
          const condition = this.formatAndHighlightNode(node.args[0])
          const trueExpr = this.formatAndHighlightNode(node.args[1])
          const falseExpr = this.formatAndHighlightNode(node.args[2])

          return `<span class="${funcClass}">if</span>${this.space}(${condition}, ${trueExpr}, ${falseExpr})`
        } else {
          // Для других функций: аргументы могут быть с переносами, если они сложные
          const argsFormatted = node.args.map(arg => {
            const argContent = this.formatAndHighlightNode(arg)
            return argContent
          }).join(`,${this.space}`)

          result = `<span class="${funcClass}">${functionName}</span>(${argsFormatted})`
        }
        return result
      }

      case 'SymbolNode': {
        const varName = node.name
        return `<span class="highlight-variable">${varName}</span>`
      }

      case 'ConstantNode': {
        return `<span class="highlight-number">${node.value.toString()}</span>`
      }

      case 'ParenthesisNode': {
        return `(<span class="highlight-punctuation">${this.formatAndHighlightNode(node.content)}</span>)`
      }

      default:
        return node.toString()
      }
    }
  },
}
</script>

<style>
#formula-block {
  white-space: pre-wrap;
  word-break: break-all;
}
#formula-block:not(.is-text) {
  overflow-x: auto;
}

/* Стили подсветки */
.highlight-variable {
  color: var(--formula-variable, #e06c75); /* Красный/оранжевый для переменных */
}

.highlight-operator {
  color: var(--formula-operator, #56b6c2); /* Голубой/бирюзовый для операторов */
}

.highlight-function {
  color: var(--formula-function, #61afef); /* Синий для функций */
}

.highlight-number {
  color: var(--formula-number, #d19a66); /* Оранжевый для чисел */
}

.highlight-punctuation {
  color: var(--formula-punctuation, #abb2bf); /* Серый для скобок, запятых */
}

.error-highlight {
  color: var(--color-error); /* Цвет ошибки */
}
</style>
